import { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import LoadingSpinner from '../UI/LoadingSpinner'
import PlanSelectionCard from '../PlanSelectionCard'
import { X, Package, Users, Calendar, DollarSign } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { plansAPI, usersAPI } from '../../services/api'
import { productsAPI } from '../../services/productsAPI'
import ContactSalesModal from '../Modals/ContactSalesModal'
import PaymentModal from './PaymentModal'

export default function SubscriptionModal({ isOpen, onClose, organizationId, subscription, onSuccess }) {
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [productModules, setProductModules] = useState([])
  const [productFeatures, setProductFeatures] = useState([])
  const [enterpriseCustomPrices, setEnterpriseCustomPrices] = useState({}) // planId -> customPrice
  const [showContactSalesModal, setShowContactSalesModal] = useState(false)
  const [contactSalesPlan, setContactSalesPlan] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [pendingSubscriptionData, setPendingSubscriptionData] = useState(null)

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      product_id: '',
      plan_id: '',
      user_limit: null, // Will be set to activeUserCount when available
      billing_cycle: 'monthly',
      status: 'active'
    }
  })

  // Fetch products
  const { data: productsData, isLoading: productsLoading, error: productsError, refetch: refetchProducts } = useQuery(
    ['products', 'subscription-modal'],
    () => api.get('/products').then(r => r.data),
    { 
      enabled: isOpen,
      retry: 1,
      staleTime: 0, // Always consider data stale
      cacheTime: 0, // Don't cache the data
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Error fetching products:', error)
        toast.error('Failed to fetch products. Please check your permissions.')
      }
    }
  )
  const products = productsData?.data?.products || []

  // Debug logging
  console.log('Products data:', productsData)
  console.log('Products loading:', productsLoading)
  console.log('Products error:', productsError)
  console.log('Products array:', products)

  // Fetch plans for selected product
  const { data: plansData, isLoading: plansLoading, refetch: refetchPlans } = useQuery(
    ['plans', selectedProduct],
    () => plansAPI.getByProduct(selectedProduct).then(r => r.data),
    {
      enabled: !!selectedProduct && isOpen,
      onError: (error) => {
        console.error('Error fetching plans:', error)
        if (error.response?.status !== 404) {
          toast.error('Failed to fetch plans')
        }
      }
    }
  )
  const plans = (plansData?.data?.plans || []).sort((a, b) => {
    const orderA = a.display_order ?? null
    const orderB = b.display_order ?? null
    
    if (orderA === null && orderB === null) {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0)
    }
    if (orderA === null) return 1
    if (orderB === null) return -1
    
    if (orderA !== orderB) {
      return orderA - orderB
    }
    
    return new Date(b.created_at || 0) - new Date(a.created_at || 0)
  })

  // Debug: Log plans data to see module information
  useEffect(() => {
    if (plans.length > 0) {
      console.log('SubscriptionModal: Plans data', plans.map(p => ({
        id: p.id,
        name: p.name,
        included_modules: p.included_modules,
        module_ids: p.module_ids,
        included_features: p.included_features,
        feature_ids: p.feature_ids
      })))
    }
  }, [plans])

  // Fetch product details (modules and features) for plan cards
  const { data: productDetails, isLoading: productDetailsLoading } = useQuery(
    ['product-details', selectedProduct],
    () => productsAPI.getDetails(selectedProduct).then(r => r.data),
    {
      enabled: !!selectedProduct && isOpen,
      onSuccess: (data) => {
        console.log('SubscriptionModal: Product details API response', {
          fullResponse: data,
          dataStructure: {
            success: data?.success,
            data: data?.data,
            product: data?.data?.product,
            modules: data?.data?.product?.modules,
            features: data?.data?.product?.features
          }
        })
      },
      onError: (error) => {
        console.error('SubscriptionModal: Error fetching product details', error)
      }
    }
  )
  // API returns: { success: true, data: { product: {...} } }
  // After .then(r => r.data): { success: true, data: { product: {...} } }
  // So product is at: data.data.product
  const product = productDetails?.data?.product || null

  // Reset form when modal opens or subscription changes
  useEffect(() => {
    if (isOpen) {
      // Force refetch products when modal opens
      refetchProducts()
      
      if (subscription) {
        // Edit mode - populate form with existing data
        setValue('product_id', subscription.product_id)
        setValue('plan_id', subscription.plan_id || '')
        setValue('user_limit', subscription.user_limit)
        setValue('billing_cycle', subscription.billing_cycle)
        setValue('status', subscription.status)
        setSelectedProduct(subscription.product_id)
        setSelectedPlan(subscription.plan_id || null)
      } else {
        // Create mode - reset form
        reset()
        setSelectedProduct(null)
        setSelectedPlan(null)
        setProductModules([])
        setProductFeatures([])
        setEnterpriseCustomPrices({})
      }
    }
  }, [isOpen, subscription, setValue, reset, refetchProducts])

  // Update product modules and features when product details are fetched
  useEffect(() => {
    console.log('SubscriptionModal: Product useEffect triggered', {
      product: product,
      productDetails: productDetails,
      productDetailsLoading: productDetailsLoading,
      selectedProduct: selectedProduct
    })
    
    if (product) {
      console.log('SubscriptionModal: Product details fetched', {
        productId: product.id,
        productName: product.name,
        modulesCount: product.modules?.length || 0,
        featuresCount: product.features?.length || 0,
        modules: product.modules,
        features: product.features,
        modulesType: typeof product.modules,
        isModulesArray: Array.isArray(product.modules),
        fullProduct: product
      })
      
      // Ensure modules and features are arrays
      const modules = Array.isArray(product.modules) ? product.modules : (product.modules ? [product.modules] : [])
      const features = Array.isArray(product.features) ? product.features : (product.features ? [product.features] : [])
      
      console.log('SubscriptionModal: Setting modules and features', {
        modulesCount: modules.length,
        featuresCount: features.length,
        moduleIds: modules.map(m => ({ id: m.id, name: m.name }))
      })
      
      setProductModules(modules)
      setProductFeatures(features)
    } else {
      console.log('SubscriptionModal: No product data available', {
        productDetails: productDetails,
        productDetailsLoading: productDetailsLoading
      })
      setProductModules([])
      setProductFeatures([])
    }
  }, [product, productDetails, productDetailsLoading])

  // Handle product selection
  const handleProductChange = (productId) => {
    console.log('Product changed to:', productId)
    setSelectedProduct(productId)
    setValue('product_id', productId)
    setSelectedPlan(null)
    setValue('plan_id', '')
  }

  // Fetch organization users to get active user count
  const { data: orgUsersData } = useQuery(
    ['organization-users', organizationId],
    () => usersAPI.getByOrganization(organizationId, { status: 'active' }).then(r => r.data),
    {
      enabled: !!organizationId && isOpen
    }
  )
  const allActiveUsers = orgUsersData?.data?.users?.filter(u => u.status === 'active') || []
  const activeUserCount = allActiveUsers.length

  // Fetch subscriptions for the organization to find product-specific users
  // Use the same query key as SubscriptionsTab to ensure cache synchronization
  const { data: orgSubscriptionsData } = useQuery(
    ['subscriptions', organizationId],
    () => api.get(`/subscriptions/organization/${organizationId}`).then(r => r.data),
    {
      enabled: !!organizationId && isOpen
    }
  )
  const orgSubscriptions = orgSubscriptionsData?.data?.subscriptions || []

  // Filter: only active products; when creating, also exclude products that already have subscriptions
  const availableProducts = useMemo(() => {
    const activeProducts = products.filter(product => product.status === 'active')
    if (subscription) {
      // When editing, show active products only (dropdown is disabled anyway)
      return activeProducts
    }
    // When creating, filter out products that already have subscriptions
    const subscribedProductIds = new Set(orgSubscriptions.map(sub => sub.product_id))
    return activeProducts.filter(product => !subscribedProductIds.has(product.id))
  }, [products, orgSubscriptions, subscription])

  // Get active users subscribed to the selected product
  const getProductActiveUserCount = () => {
    if (!selectedProduct) return 0
    
    // Find subscriptions for the selected product in this organization
    const productSubscriptions = orgSubscriptions.filter(sub => 
      sub.product_id === selectedProduct
    )
    
    if (productSubscriptions.length === 0) return 0
    
    // Get subscription IDs for this product
    const productSubscriptionIds = productSubscriptions.map(sub => sub.id)
    
    // Count active users who have these subscription IDs
    const productActiveUsers = allActiveUsers.filter(user => 
      user.subscription_id && productSubscriptionIds.includes(user.subscription_id)
    )
    
    return productActiveUsers.length
  }

  const productActiveUserCount = getProductActiveUserCount()

  // Get selected plan object (must be declared before useEffect that uses it)
  const selectedPlanObj = plans.find(p => p.id === selectedPlan) || null

  // Set user_limit and billing_cycle when plan is selected
  useEffect(() => {
    if (selectedPlanObj) {
      setValue('user_limit', selectedPlanObj.max_users !== null && selectedPlanObj.max_users !== undefined ? selectedPlanObj.max_users : null)
      // Free plans: ensure billing_cycle is set (UI is hidden)
      if (isPlanFree(selectedPlanObj)) {
        setValue('billing_cycle', 'monthly')
      }
    } else if (activeUserCount > 0) {
      setValue('user_limit', activeUserCount)
    }
  }, [selectedPlanObj, activeUserCount, setValue])

  // Parse included_modules for display
  const getModuleCount = (plan) => {
    if (!plan) return 0
    let includedModules = plan.included_modules || plan.module_ids || []
    if (typeof includedModules === 'string') {
      try {
        includedModules = JSON.parse(includedModules)
      } catch (e) {
        includedModules = []
      }
    }
    return Array.isArray(includedModules) ? includedModules.length : 0
  }

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan.id)
    setValue('plan_id', plan.id)
    
    // Always set user_limit to match the selected plan's max_users (even if null or 0)
    setValue('user_limit', plan.max_users !== null && plan.max_users !== undefined ? plan.max_users : null)
  }

  const handleEnterpriseCustomPriceChange = (planId, value) => {
    setEnterpriseCustomPrices(prev => ({
      ...prev,
      [planId]: value ? parseFloat(value) : null
    }))
  }


  // Calculate total price based on selected plan, billing cycle, and active users subscribed to the product
  const calculateTotalPrice = () => {
    if (!selectedPlanObj) return null
    
    // Check if Enterprise plan with custom price
    if (selectedPlanObj.is_enterprise) {
      const customPrice = enterpriseCustomPrices[selectedPlanObj.id]
      if (customPrice && customPrice > 0) {
        return {
          basePrice: customPrice,
          userLimit: null,
          billingCycle: watch('billing_cycle') || 'monthly',
          totalPrice: customPrice,
          currency: selectedPlanObj.currency || 'USD',
          pricingModel: 'fixed'
        }
      }
      return null // Enterprise plan without custom price
    }
    
    const billingCycle = watch('billing_cycle') || 'monthly'
    
    // Get selected product to check pricing model
    const selectedProductObj = products.find(p => p.id === selectedProduct) || product
    
    let basePrice = 0
    if (billingCycle === 'monthly') {
      basePrice = parseFloat(selectedPlanObj.monthly_price) || 0
    } else if (billingCycle === 'yearly') {
      basePrice = parseFloat(selectedPlanObj.yearly_price) || 0
    }
    
    // Only calculate if we have a price
    if (basePrice === 0) return null
    
    // If pricing model is per_user, multiply by active users subscribed to this product
    // Otherwise, use base price as-is (fixed pricing)
    const pricingModel = selectedProductObj?.pricing_model || 'fixed'
    let totalPrice = basePrice
    let userCountForCalculation = null
    
    if (pricingModel === 'per_user') {
      // For per_user pricing, use active users subscribed to this product
      userCountForCalculation = productActiveUserCount
      if (userCountForCalculation > 0) {
        totalPrice = basePrice * userCountForCalculation
      } else {
        // If no active users, can't calculate total price for per_user model
        return null
      }
    }
    
    return {
      basePrice,
      userLimit: userCountForCalculation,
      billingCycle,
      totalPrice,
      currency: selectedPlanObj.currency || 'USD',
      pricingModel
    }
  }

  const priceCalculation = calculateTotalPrice()

  // Format price with currency
  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price)
  }

  // Calculate savings percentage for yearly billing
  const calculateSavings = () => {
    if (!selectedPlanObj) return null
    const monthlyPrice = parseFloat(selectedPlanObj.monthly_price) || 0
    const yearlyPrice = parseFloat(selectedPlanObj.yearly_price) || 0
    if (monthlyPrice === 0 || yearlyPrice === 0) return null
    const monthlyYearlyTotal = monthlyPrice * 12
    if (yearlyPrice >= monthlyYearlyTotal) return null
    const savings = monthlyYearlyTotal - yearlyPrice
    const savingsPercent = Math.round((savings / monthlyYearlyTotal) * 100)
    return savingsPercent
  }

  // Create/Update subscription mutation
  const subscriptionMutation = useMutation(
    (data) => {
      const url = subscription ? `/subscriptions/${subscription.id}` : '/subscriptions'
      const method = subscription ? 'put' : 'post'
      return api[method](url, { ...data, organization_id: organizationId }).then(r => r.data)
    },
    {
      onSuccess: (data) => {
        toast.success(subscription ? 'Subscription updated successfully' : 'Subscription created successfully')
        onSuccess?.(data)
        onClose()
      },
      onError: (error) => {
        console.error('Subscription error:', error)
        toast.error(error.response?.data?.message || 'Failed to save subscription')
      }
    }
  )

  // Check if plan is free
  const isPlanFree = (plan) => {
    if (!plan) return false
    const monthlyPrice = parseFloat(plan.monthly_price) || 0
    const yearlyPrice = parseFloat(plan.yearly_price) || 0
    return monthlyPrice === 0 && yearlyPrice === 0
  }

  const onSubmit = (data) => {
    console.log('Submitting subscription data:', data)
    if (!selectedPlan) {
      toast.error('Please select a plan')
      return
    }
    
    // Check if Enterprise plan - redirect to Contact Us
    const selectedPlanObj = plans.find(p => p.id === selectedPlan)
    if (selectedPlanObj?.is_enterprise) {
      // Get custom price if entered
      const customPrice = enterpriseCustomPrices[selectedPlan]
      // Open Contact Us modal with plan and custom price
      setContactSalesPlan({ ...selectedPlanObj, customPrice })
      setShowContactSalesModal(true)
      return
    }
    
    // If editing existing subscription
    if (subscription) {
      // Check if plan has changed to a paid plan
      const planChanged = subscription.plan_id !== selectedPlan
      const isPaidPlan = !isPlanFree(selectedPlanObj)
      
      // If plan changed to a paid plan, show payment modal
      if (planChanged && isPaidPlan) {
        // Show payment modal for upgrade
        setShowPaymentModal(true)
        return
      }
      
      // If plan is free or same plan, proceed directly with update
      const calculatedPrice = priceCalculation?.totalPrice || null
      subscriptionMutation.mutate({
        ...data,
        calculated_price: calculatedPrice
      })
      return
    }
    
    // For new subscriptions, check if plan is free
    if (isPlanFree(selectedPlanObj)) {
      // Free plan - create subscription directly
      const calculatedPrice = priceCalculation?.totalPrice || null
      subscriptionMutation.mutate({
        ...data,
        calculated_price: calculatedPrice
      })
    } else {
      // Non-free plan - show payment modal first
      const calculatedPrice = priceCalculation?.totalPrice || null
      setPendingSubscriptionData({
        ...data,
        calculated_price: calculatedPrice
      })
      setShowPaymentModal(true)
    }
  }

  const handleClose = () => {
    reset()
    setSelectedProduct(null)
    setSelectedPlan(null)
    setProductModules([])
    setProductFeatures([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">
            {subscription ? 'Edit Subscription' : 'Add New Subscription'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Product Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Product <span className="text-red-500">*</span>
              </label>
              {productsLoading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-secondary-500">Loading products...</span>
                </div>
              ) : availableProducts.length === 0 ? (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary-500">
                    {products.length === 0 
                      ? 'No products available' 
                      : products.every(p => p.status !== 'active')
                        ? 'No active products available'
                        : 'All available products are already subscribed'}
                  </span>
                  {products.length === 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => refetchProducts()}
                      disabled={productsLoading}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              ) : (
                <select
                  value={selectedProduct || ''}
                  onChange={(e) => handleProductChange(e.target.value)}
                  disabled={!!subscription} // Disable product change in edit mode
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-secondary-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a product</option>
                  {availableProducts.map((product) => {
                    // Format product display: show price and pricing model only if they exist
                    const priceDisplay = product.base_price != null ? `$${product.base_price}` : ''
                    const pricingModelDisplay = product.pricing_model || ''
                    const priceInfo = priceDisplay && pricingModelDisplay 
                      ? ` - ${priceDisplay} (${pricingModelDisplay})`
                      : priceDisplay 
                        ? ` - ${priceDisplay}`
                        : pricingModelDisplay
                          ? ` - (${pricingModelDisplay})`
                          : ''
                    
                    return (
                      <option key={product.id} value={product.id}>
                        {product.name}{priceInfo}
                      </option>
                    )
                  })}
                </select>
              )}
              {productsError && (
                <p className="text-sm text-red-500">
                  Failed to load products. Please check your permissions.
                </p>
              )}
            </div>

            {/* Plan Selection */}
            {selectedProduct && selectedProduct !== '' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-secondary-700">
                  Select Plan <span className="text-red-500">*</span>
                </label>
                {plansLoading || productDetailsLoading ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-secondary-500">
                      {plansLoading ? 'Loading plans...' : 'Loading product details...'}
                    </span>
                  </div>
                 ) : plans.length > 0 ? (
                   <>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                       {plans.map((plan) => {
                         console.log('SubscriptionModal: Rendering plan card', {
                           planId: plan.id,
                           planName: plan.name,
                           productModulesCount: productModules.length,
                           productFeaturesCount: productFeatures.length,
                           productModules: productModules,
                           planIncludedModules: plan.included_modules,
                           planModuleIds: plan.module_ids
                         })
                         return (
                           <PlanSelectionCard
                             key={plan.id}
                             plan={plan}
                             productModules={productModules}
                             productFeatures={productFeatures}
                             isSelected={selectedPlan === plan.id}
                             onSelect={() => handlePlanSelect(plan)}
                             customPrice={enterpriseCustomPrices[plan.id]}
                             onCustomPriceChange={handleEnterpriseCustomPriceChange}
                             onContactUsClick={(plan, customPrice) => {
                               setContactSalesPlan({ ...plan, customPrice })
                               setShowContactSalesModal(true)
                             }}
                           />
                         )
                       })}
                     </div>
                     <p className="text-xs text-secondary-500 mt-2">
                       Click on a plan card above to select it
                     </p>
                   </>
                 ) : (
                  <p className="text-sm text-secondary-500">No plans available for this product</p>
                )}
                {errors.plan_id && (
                  <p className="text-sm text-red-500">{errors.plan_id.message}</p>
                )}
              </div>
            )}

            {/* Billing Cycle - only show for paid plans (not free, not enterprise) */}
            {selectedPlanObj && !isPlanFree(selectedPlanObj) && !selectedPlanObj.is_enterprise && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-secondary-700">
                  Billing Cycle <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('billing_cycle', { required: 'Billing cycle is required' })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
                {errors.billing_cycle && (
                  <p className="text-sm text-red-500">{errors.billing_cycle.message}</p>
                )}
              </div>
            )}

            {/* Plan Details - for free plan show simple message; for paid plans show pricing */}
            {selectedPlanObj && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                {/* Plan Header */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-base font-bold text-blue-900 capitalize">{selectedPlanObj.name}</h4>
                    {selectedPlanObj.is_popular && (
                      <span className="text-xs px-2 py-0.5 bg-blue-200 text-blue-800 rounded">Best for User</span>
                    )}
                  </div>
                  {getModuleCount(selectedPlanObj) > 0 && (
                    <p className="text-xs text-blue-700">Includes {getModuleCount(selectedPlanObj)} {getModuleCount(selectedPlanObj) === 1 ? 'module' : 'modules'}</p>
                  )}
                </div>

                {selectedPlanObj.is_enterprise ? (
                  <p className="text-sm text-blue-700 font-medium">Contact us for custom pricing</p>
                ) : isPlanFree(selectedPlanObj) ? (
                  <p className="text-sm text-blue-700 font-medium">Free plan — no payment required</p>
                ) : (
                  <>
                    {/* Pricing Options - only for paid plans */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Monthly Price */}
                      <div className={`p-3 bg-white rounded-md border-2 transition-colors ${
                        watch('billing_cycle') === 'monthly' ? 'border-blue-500 shadow-sm' : 'border-gray-200'
                      }`}>
                        <p className="text-xs text-gray-600 mb-1">Monthly Price</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatPrice(selectedPlanObj.monthly_price || 0, selectedPlanObj.currency)}/month
                        </p>
                      </div>

                      {/* Yearly Price */}
                      <div className={`p-3 bg-white rounded-md border-2 transition-colors ${
                        watch('billing_cycle') === 'yearly' ? 'border-blue-500 shadow-sm' : 'border-gray-200'
                      }`}>
                        <p className="text-xs text-gray-600 mb-1">Yearly Price</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatPrice(selectedPlanObj.yearly_price || 0, selectedPlanObj.currency)}/year
                        </p>
                        {calculateSavings() && calculateSavings() > 0 && (
                          <p className="text-xs text-green-600 mt-1 font-medium">Save {calculateSavings()}%</p>
                        )}
                      </div>
                    </div>

                    {/* Selected Billing Summary */}
                    {priceCalculation ? (
                      <div className="pt-3 border-t border-blue-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-blue-700">Selected Billing:</span>
                          <span className="text-sm font-medium text-blue-900 capitalize">
                            {watch('billing_cycle') || 'monthly'}
                          </span>
                        </div>
                        {priceCalculation.pricingModel === 'per_user' && priceCalculation.userLimit ? (
                          <>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-blue-700">Active Users (Product):</span>
                              <span className="text-sm font-medium text-blue-900">
                                {priceCalculation.userLimit} users
                              </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-blue-700">Calculation:</span>
                              <span className="text-sm font-medium text-blue-900">
                                {formatPrice(priceCalculation.basePrice, priceCalculation.currency)} × {priceCalculation.userLimit} users
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-blue-700">Pricing Model:</span>
                            <span className="text-sm font-medium text-blue-900 capitalize">
                              Fixed Price
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                          <span className="text-base font-semibold text-blue-900">Total:</span>
                          <span className="text-xl font-bold text-blue-900">
                            = {formatPrice(priceCalculation.totalPrice, priceCalculation.currency)} / {priceCalculation.billingCycle}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="pt-3 border-t border-blue-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-blue-700">Selected Billing:</span>
                          <span className="text-sm font-medium text-blue-900 capitalize">
                            {watch('billing_cycle') || 'monthly'}
                          </span>
                        </div>
                        {(() => {
                          const selectedProductObj = products.find(p => p.id === selectedProduct) || product
                          const pricingModel = selectedProductObj?.pricing_model || 'fixed'
                          if (pricingModel === 'per_user') {
                            return (
                              <div className="text-sm text-blue-600 italic mt-2">
                                Price will be calculated when users are subscribed to this product
                              </div>
                            )
                          } else {
                            return (
                              <div className="text-sm text-blue-600 italic mt-2">
                                Price will be calculated when plan is selected
                              </div>
                            )
                          }
                        })()}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
              {errors.status && (
                <p className="text-sm text-red-500">{errors.status.message}</p>
              )}
            </div>


            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={subscriptionMutation.isLoading || !selectedProduct || !selectedPlan}
              >
                {subscriptionMutation.isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : subscription ? (
                  'Update Subscription'
                ) : (
                  'Create Subscription'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Contact Us Modal */}
      <ContactSalesModal
        isOpen={showContactSalesModal}
        onClose={() => {
          setShowContactSalesModal(false)
          setContactSalesPlan(null)
        }}
        plan={contactSalesPlan}
        organizationId={organizationId}
      />

      {/* Payment Modal - for new subscriptions with non-free plans or subscription upgrades */}
      {showPaymentModal && selectedPlanObj && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false)
            setPendingSubscriptionData(null)
          }}
          subscription={subscription || null} // Existing subscription for upgrades, null for new subscriptions
          upgradePlan={selectedPlanObj}
          subscriptionData={pendingSubscriptionData} // Pass the subscription data to create after payment (only for new subscriptions)
          organizationId={organizationId}
          onSuccess={(paymentData) => {
            // Payment and subscription creation/upgrade are handled in PaymentModal
            // Just close modals and refresh
            setShowPaymentModal(false)
            setPendingSubscriptionData(null)
            onSuccess?.(paymentData)
            onClose()
          }}
        />
      )}
    </div>
  )
}
